import { useCallback, useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconTeam } from '@components/icons/team';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIsMobile } from '@hooks/use-is-mobile';
import { sortByObjectKey } from '@utils/collections';

import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';
import { IApplication, IUser } from 'models/types';

export const ApplicationAccess = observer(({ application }: { application: IApplication }) => {
  const { t } = useStore();
  const { showModal } = useModal();
  const [revoking, setRevoking] = useState<Record<number, boolean>>({});
  const { hasRolePermission } = usePermissions();

  const onAddUserClick = useCallback(() => {
    showModal('ADD_APPLICATION_ACCESS', {
      applicationId: application.id,
    });
  }, [application, showModal]);

  const onRevokeClick = useCallback(
    async (follower: IUser) => {
      setRevoking((old) => ({ ...old, [follower.id]: true }));
      await application.removeFollowerAccess(follower);
      setRevoking((old) => ({
        ...old,
        [follower.id]: false,
      }));
    },
    [application]
  );

  const sortedHiringTeam: Array<IUser> = sortByObjectKey(application.job.activeFollowers, 'name');
  const sortedApplicationFollowers: Array<IUser> = sortByObjectKey(application.followers, 'name');

  return (
    <Stack space={6} style={{ marginTop: '1.5rem' }}>
      <Stack space={2}>
        <Text font="bold14" text={t('application.access.header')} />

        {sortedHiringTeam.map((follower) => (
          <FollowerCard
            key={follower.id}
            follower={follower}
            isRevoking={Boolean(revoking[follower.id])}
            isApplicationFollower={application.followers.includes(follower)}
            onRevoke={onRevokeClick}
          />
        ))}
      </Stack>

      <Separator />

      <Stack space={2}>
        <Text font="bold14" color="purple100" text={t('application.header.access')} />

        {sortedApplicationFollowers.map((follower) => (
          <FollowerCard
            key={follower.id}
            follower={follower}
            isRevoking={Boolean(revoking[follower.id])}
            isApplicationFollower={application.followers.includes(follower)}
            onRevoke={onRevokeClick}
          />
        ))}

        {sortedApplicationFollowers.length === 0 && (
          <Card variant="gray" space={4}>
            <Stack space={4} orientation="horizontal" align="center">
              <IconTeam color="purple60" />
              <Text font="semibold14" color="purple80" text={t('application.access.noaccess')} />
            </Stack>
          </Card>
        )}
      </Stack>

      {hasRolePermission('application:grant-access') && (
        <div style={{ display: 'flex' }}>
          <Button
            variant="secondary"
            onClick={onAddUserClick}
            iconLeft={<IconAddPlus />}
            text={t('application.access.button.submit')}
          />
        </div>
      )}
    </Stack>
  );
});

const FollowerCard = observer(
  ({
    follower,
    onRevoke,
    isRevoking,
    isApplicationFollower,
  }: {
    follower: IUser;
    onRevoke(follower: IUser): void;
    isRevoking: boolean;
    isApplicationFollower: boolean;
  }) => {
    const isMobile = useIsMobile();
    const { hasRolePermission } = usePermissions();
    const { t } = useStore();

    return (
      <Card spaceY={2} spaceX={isMobile ? 2 : 4} variant="default" key={follower.id}>
        <Stack space={3} orientation="horizontal" align="center" wrap>
          <Avatar src={follower.picture} name={follower.name} size="md" />

          <div style={{ flexGrow: 1 }}>
            <Text font="semibold14" color="purple100" text={follower.name} />
            <Text font="regular12" color="purple80" text={follower.email} />
            <Text font="regular12" color="purple80" text={follower.roleDisplay} />
          </div>

          {isApplicationFollower && hasRolePermission('application:revoke-access') && (
            <Button
              variant="secondary"
              isDisabled={isRevoking}
              onClick={() => onRevoke(follower)}
              text={isRevoking ? t('application.access.revoking') : t('application.access.revoke')}
            />
          )}
        </Stack>
      </Card>
    );
  }
);
